import React, {createContext, useMemo, type ReactNode} from 'react';
import {
  Configuration,
  CompanyApi,
  UserApi,
  KeywordApi,
  PressKitApi,
  ProofreadingApi,
} from '@openapi/prtimes';
import {
  PressKitApi as PressKitAdminApi,
  Configuration as AdminConfiguration,
  AdminCompanyApi,
  MediaListApi,
} from '@openapi/prtimes-admin-company';
import {
  PriceApi,
  Configuration as PriceConfiguration,
} from '@openapi/prtimes-price';
import {
  PressReleaseEditorApi,
  Configuration as PressReleaseEditorConfiguration,
  LegacyPressReleaseEditorApi,
  PressReleaseDistributorApi,
} from '@openapi/prtimes-press-release-editor';
import {
  ProofreadingApi as ProofreadingV3Api,
  Configuration as ProofreadingV3Configuration,
} from '@openapi/prtimes-proofreading';
import {
  ClippingBillingApi,
  Configuration as ClippingBillingConfiguration,
} from '@openapi/prtimes-clipping-billing';
import {
  ArticlePastSearchApi,
  Configuration as ArticlePastSearchConfiguration,
} from '@openapi/clipping';
import {
  MediaUserApi,
  Configuration as MediaUserConfiguration,
} from '@openapi/prtimes-media-user';
import {Configuration as StoryConfiguration, StoryApi} from '@openapi/story';
import {
  PrtimesMediaListApi as MediaRelationApi,
  Configuration as MediaRelationConfiguration,
} from '@openapi/prtimes-media-list';
import {
  API_URL,
  PRESS_KIT_ADMIN_URL,
  PRESS_KIT_URL,
  PRICE_URL,
  PRESS_RELEASE_EDITOR_URL,
} from '@/constants';
import {clippingBillingBasePath} from '@/features/price/constants/clipping-billing';

/**
 * Company Detail Configuration
 */
const config = new Configuration({
  basePath: API_URL,
  credentials: 'same-origin',
});
export const companyApi = new CompanyApi(config);
export const userApi = new UserApi(config);
export const keywordApi = new KeywordApi(config);

/**
 * PressKit Configuration
 */
const pressKitConfig = new Configuration({
  basePath: PRESS_KIT_URL,
});
export const pressKitApi = new PressKitApi(pressKitConfig);

/**
 * PressKit Admin Configuration
 */
const pressKitAdminConfig = new AdminConfiguration({
  basePath: PRESS_KIT_ADMIN_URL,
});
export const pressKitAdminApi = new PressKitAdminApi(pressKitAdminConfig);
export const adminCompanyApi = new AdminCompanyApi(pressKitAdminConfig);

/**
 * Price Configuration
 */
const priceApiConfig = new PriceConfiguration({
  basePath: PRICE_URL,
});
export const priceApi = new PriceApi(priceApiConfig);

/**
 * Press Release Editor Configuration
 */
const pressReleaseEditorConfig = new PressReleaseEditorConfiguration({
  basePath: PRESS_RELEASE_EDITOR_URL,
  credentials: 'same-origin',
});
export const pressReleaseEditorApi = new PressReleaseEditorApi(
  pressReleaseEditorConfig,
);
export const legacyPressReleaseEditorApi = new LegacyPressReleaseEditorApi(
  new PressReleaseEditorConfiguration({
    basePath: '/api',
    credentials: 'same-origin',
  }),
);
export const pressReleaseDistributorApi = new PressReleaseDistributorApi(
  pressReleaseEditorConfig,
);
export const proofreadingV3Api = new ProofreadingV3Api(
  new ProofreadingV3Configuration({
    basePath: '/api/proofreading.php',
    credentials: 'same-origin',
  }),
);

const clippingBillingConfig = new ClippingBillingConfiguration({
  basePath: clippingBillingBasePath,
  credentials: 'same-origin',
});
export const clippingBillingApi = new ClippingBillingApi(clippingBillingConfig);

const articlePastSearchConfig = new ArticlePastSearchConfiguration({
  basePath: '',
  credentials: 'same-origin',
});
export const articlePastSearchApi = new ArticlePastSearchApi(
  articlePastSearchConfig,
);

/**
 * リプレイス後のMedia ListのAPI
 */
export const mediaListApi = new MediaListApi(
  new AdminConfiguration({
    basePath: '/api/press_kit_edit.php',
  }),
);

/**
 * レガシーなMedia ListのAPI
 */
export const legacyMediaListApi = new MediaListApi(
  new AdminConfiguration({
    basePath: '',
  }),
);

/**
 * リプレイス後のMedia UserのAPI
 */
export const mediaUserApi = new MediaUserApi(
  new MediaUserConfiguration({
    basePath: '/api/media_user.php',
  }),
);

/**
 * リプレイス後のMedia RelationのAPI
 */
export const mediaRelationApi = new MediaRelationApi(
  new MediaRelationConfiguration({
    basePath: '/api/media_list.php',
  }),
);

export const proofreadingApi = new ProofreadingApi(
  new Configuration({
    basePath: '',
  }),
);

/**
 * プレスリリースページ埋め込み用のStoryApi
 */
export const storyApi = new StoryApi(
  new StoryConfiguration({
    basePath: '',
    credentials: 'same-origin',
  }),
);

type ContextProps = {
  readonly companyApi: CompanyApi;
  readonly userApi: UserApi;
  readonly keywordApi: KeywordApi;
  readonly pressKitApi: PressKitApi;
  readonly pressKitAdminApi: PressKitAdminApi;
  readonly adminCompanyApi: AdminCompanyApi;
  readonly priceApi: PriceApi;
  readonly pressReleaseEditorApi: PressReleaseEditorApi;
  readonly clippingBillingApi: ClippingBillingApi;
  readonly articlePastSearchApi: ArticlePastSearchApi;
  readonly storyApi: StoryApi;
};

export const ApiContext = createContext<ContextProps>({
  companyApi,
  userApi,
  keywordApi,
  pressKitApi,
  pressKitAdminApi,
  adminCompanyApi,
  priceApi,
  pressReleaseEditorApi,
  clippingBillingApi,
  articlePastSearchApi,
  storyApi,
});

type Props = {
  readonly children: ReactNode;
};

// eslint-disable-next-line react/function-component-definition
const ApiProvider: React.FC<Props> = ({children}) => {
  const provideValue = useMemo(
    () => ({
      companyApi,
      userApi,
      keywordApi,
      pressKitApi,
      pressKitAdminApi,
      adminCompanyApi,
      priceApi,
      pressReleaseEditorApi,
      clippingBillingApi,
      articlePastSearchApi,
      storyApi,
    }),
    [],
  );

  return (
    <ApiContext.Provider value={provideValue}>{children}</ApiContext.Provider>
  );
};

export default ApiProvider;
